import { Route, RouteComponentProps, Switch } from "react-router-dom"
import { useTask } from "../../../tasks/hooks/use-task"
import CircularProgress from "@material-ui/core/CircularProgress"
import TaskBasicRoute from "./basic"
import TaskCalendarRoute from "./calendar"
import TaskKanbanRoute from "./kanban"
import TaskSubtasksRoute from "./subtasks"
import TaskTodosRoute from "./todos"
import TaskReportedHoursRoute from "./reported-hours"
import { paths } from "../../../paths"
import TaskNavbar from "../../../tasks/components/Navbar/TaskNavbar"
import ErrorList from "../../../components/ErrorList"
import { useTranslation } from "react-i18next"
import { ProjectCalendarSettingsProvider } from "../../providers/project-calendar-settings"

type RouteParams = { taskId: string }

const TaskLayout = ({ match, location }: RouteComponentProps<RouteParams>) => {
  const translations = useTranslations()
  const { taskId } = match.params
  const { task, isLoading: isLoadingTask, isError: isErrorTask } = useTask(taskId)

  if (isLoadingTask) return <CircularProgress />
  if (isErrorTask) return <ErrorList errors={[translations.fetchErrorTask]} />
  if (!task) return <ErrorList errors={[translations.taskNotFoundError]} />

  return (
    <ProjectCalendarSettingsProvider
      projectId={task.projectId}
      loadingFallback={<CircularProgress />}
      errorFallback={<ErrorList errors={[translations.fetchErrorCalendarSettings]} />}
    >
      <TaskLayoutContent location={location} task={task} />
    </ProjectCalendarSettingsProvider>
  )
}

const TaskLayoutContent = ({
  location,
  task,
}: Pick<RouteComponentProps<RouteParams>, "location"> & { task: NonNullable<ReturnType<typeof useTask>["task"]> }) => {
  return (
    <>
      <TaskNavbar
        task={task}
        isFullWidth={!location.pathname.includes("basic") && !location.pathname.includes("reported-hours")}
      />
      <Switch>
        <Route path={paths.taskBasic()} component={TaskBasicRoute} />
        <Route path={paths.taskSubtasks()} component={TaskSubtasksRoute} />
        <Route path={paths.taskTodos()} component={TaskTodosRoute} />
        <Route path={paths.taskCalendar()} component={TaskCalendarRoute} />
        <Route path={paths.taskKanban()} component={TaskKanbanRoute} />
        <Route path={paths.taskReportedHours()} component={TaskReportedHoursRoute} />
      </Switch>
    </>
  )
}

const defaultTranslations = {
  taskNotFoundError: "Task not found",
  fetchErrorTask: "Failed to fetch task",
  fetchErrorCalendarSettings: "Failed to fetch calendar settings",
}

const useTranslations = (): typeof defaultTranslations => {
  const { t } = useTranslation("translation")

  return {
    taskNotFoundError: t("taskNotFoundError", defaultTranslations.taskNotFoundError),
    fetchErrorTask: t("fetchErrorTask", defaultTranslations.fetchErrorTask),
    fetchErrorCalendarSettings: t("fetchErrorCalendarSettings", defaultTranslations.fetchErrorCalendarSettings),
  }
}

export default TaskLayout
